import React, { useState, useMemo } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, eachMonthOfInterval, isToday, startOfYear, endOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const TimelineView = ({ goals, onView, onEdit, onDelete, language = 'pt' }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedDayGoals, setSelectedDayGoals] = useState([]);
  const [showDayModal, setShowDayModal] = useState(false);
  const [popoverStates, setPopoverStates] = useState({});

  const statusColors = {
    planejado: 'bg-blue-500',
    em_progresso: 'bg-yellow-500',
    com_mentoria: 'bg-purple-500',
    acompanhamento: 'bg-orange-500',
    concluido: 'bg-green-500',
    arquivado: 'bg-gray-500'
  };

  const getLabels = () => {
    const labels = {
      pt: {
        monthlyView: 'Visão Mensal',
        dailyView: 'Visão Diária',
        goToToday: 'Ir para Hoje',
        status: 'Status',
        planned: 'Planejado',
        inProgress: 'Em Progresso',
        withMentor: 'Com Mentoria',
        followUp: 'Acompanhamento',
        statusCompleted: 'Concluído',
        archived: 'Arquivado',
        goalsOfMonth: 'Metas do Mês',
        upcoming: 'Próximas (7 dias)',
        sectionCompleted: 'Concluídas',
        more: 'mais'
      },
      en: {
        monthlyView: 'Monthly View',
        dailyView: 'Daily View',
        goToToday: 'Go to Today',
        status: 'Status',
        planned: 'Planned',
        inProgress: 'In Progress',
        withMentor: 'With Mentor',
        followUp: 'Follow-up',
        statusCompleted: 'Completed',
        archived: 'Archived',
        goalsOfMonth: 'Goals this Month',
        upcoming: 'Upcoming (7 days)',
        sectionCompleted: 'Completed',
        more: 'more'
      }
    };
    return labels?.[language] || labels?.pt;
  };

  const labels = getLabels();

  const viewModeOptions = [
    { value: 'monthly', label: labels?.monthlyView },
    { value: 'daily', label: labels?.dailyView }
  ];

  const groupedGoals = useMemo(() => {
    const grouped = {};
    
    goals?.forEach(goal => {
      if (goal?.dataAlvo) {
        const date = parseISO(goal?.dataAlvo);
        let dateKey;
        
        if (viewMode === 'monthly') {
          dateKey = format(date, 'yyyy-MM');
        } else {
          dateKey = format(date, 'yyyy-MM-dd');
        }
        
        if (!grouped?.[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped?.[dateKey]?.push(goal);
      }
    });

    return grouped;
  }, [goals, viewMode]);

  const timelineDates = useMemo(() => {
    if (viewMode === 'monthly') {
      const start = startOfYear(selectedDate);
      const end = endOfYear(selectedDate);
      return eachMonthOfInterval({ start, end });
    } else {
      const start = startOfMonth(selectedDate);
      const end = endOfMonth(selectedDate);
      return eachDayOfInterval({ start, end });
    }
  }, [selectedDate, viewMode]);

  const getGoalsForDate = (date) => {
    let dateKey;
    if (viewMode === 'monthly') {
      dateKey = format(date, 'yyyy-MM');
    } else {
      dateKey = format(date, 'yyyy-MM-dd');
    }
    return groupedGoals?.[dateKey] || [];
  };

  const scrollToToday = () => {
    const todayElement = document.getElementById('timeline-today');
    if (todayElement) {
      todayElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'monthly') {
      newDate?.setFullYear(newDate?.getFullYear() + direction);
    } else {
      newDate?.setMonth(newDate?.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const getStatusLabels = () => ({
    planejado: labels?.planned,
    em_progresso: labels?.inProgress,
    com_mentoria: labels?.withMentor,
    acompanhamento: labels?.followUp,
    concluido: labels?.statusCompleted,
    arquivado: labels?.archived
  });

  const statusLabels = getStatusLabels();

  const GoalTimelineCard = ({ goal, date, isInModal = false }) => {
    const popoverId = `popover-${goal?.id}-${format(date, 'yyyy-MM-dd')}`;
    
    return (
      <div className="relative">
        <div 
          className={`w-4 h-4 rounded-full ${statusColors?.[goal?.status] || 'bg-gray-500'} 
                     cursor-pointer hover:scale-125 transition-transform duration-200
                     border-2 border-white shadow-md`}
          onClick={() => {
            if (isInModal) {
              onView(goal);
            } else {
              setPopoverStates(prev => ({
                ...prev,
                [popoverId]: !prev?.[popoverId]
              }));
            }
          }}
          title={goal?.titulo}
        />
        {popoverStates?.[popoverId] && !isInModal && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setPopoverStates(prev => ({ ...prev, [popoverId]: false }))}
            />
            
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 
                            bg-card border border-border rounded-lg shadow-xl p-4 min-w-[250px] sm:min-w-[300px]
                            z-50 max-w-[90vw]">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-foreground pr-2">{goal?.titulo}</h4>
                
                {goal?.responsavel && (
                  <p className="text-xs text-muted-foreground flex items-center space-x-1">
                    <Icon name="User" size={12} />
                    <span>{goal?.responsavel}</span>
                  </p>
                )}
                
                {goal?.orcamento && (
                  <p className="text-xs text-muted-foreground flex items-center space-x-1">
                    <Icon name="DollarSign" size={12} />
                    <span>{formatCurrency(goal?.orcamento)}</span>
                  </p>
                )}
                
                <div className="flex flex-wrap gap-1 pt-2 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onView(goal);
                      setPopoverStates(prev => ({ ...prev, [popoverId]: false }));
                    }}
                    className="h-8 px-2 text-xs hover:bg-blue-100"
                  >
                    {language === 'pt' ? 'Ver' : 'View'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onEdit(goal);
                      setPopoverStates(prev => ({ ...prev, [popoverId]: false }));
                    }}
                    className="h-8 px-2 text-xs hover:bg-yellow-100"
                  >
                    {language === 'pt' ? 'Editar' : 'Edit'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onDelete(goal?.id);
                      setPopoverStates(prev => ({ ...prev, [popoverId]: false }));
                    }}
                    className="h-8 px-2 text-xs hover:bg-red-100 text-red-600"
                  >
                    {language === 'pt' ? 'Excluir' : 'Delete'}
                  </Button>
                </div>
              </div>
              
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 
                              w-0 h-0 border-l-4 border-r-4 border-t-4 
                              border-l-transparent border-r-transparent border-t-border" />
            </div>
          </>
        )}
      </div>
    );
  };

  const handleDayClick = (date) => {
    if (viewMode === 'monthly') {
      setSelectedDate(date);
      setViewMode('daily');
    } else {
      const dayGoals = getGoalsForDate(date);
      if (dayGoals?.length > 0) {
        setSelectedDayGoals(dayGoals);
        setShowDayModal(true);
      }
    }
  };

  const handleMonthHover = (date, isEntering = true) => {
    if (viewMode !== 'monthly') return;
    
    const monthKey = format(date, 'yyyy-MM');
    if (isEntering) {
      const monthGoals = Object.entries(groupedGoals)
        ?.filter(([key]) => key?.startsWith(monthKey))
        ?.reduce((acc, [dateKey, goals]) => {
          const day = format(parseISO(dateKey + '-01'), 'd');
          acc[day] = goals?.length;
          return acc;
        }, {});
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const numericValue = parseFloat(value) || 0;
    return new Intl.NumberFormat(language === 'pt' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: language === 'pt' ? 'BRL' : 'USD',
      minimumFractionDigits: 2,
    })?.format(numericValue);
  };

  const DayGoalsModal = () => {
    if (!showDayModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">
              {language === 'pt' ? 'Metas do Dia' : 'Daily Goals'}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => setShowDayModal(false)}
            />
          </div>
          
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
            <div className="grid gap-3">
              {selectedDayGoals?.map(goal => (
                <div key={goal?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border">
                  <div className="flex items-center space-x-3">
                    <GoalTimelineCard goal={goal} date={new Date()} isInModal={true} />
                    <div>
                      <h4 className="font-medium text-foreground">{goal?.titulo}</h4>
                      {goal?.responsavel && (
                        <p className="text-sm text-muted-foreground">{goal?.responsavel}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => {
                        onView(goal);
                        setShowDayModal(false);
                      }}
                      className="h-8 w-8 p-0 hover:bg-blue-100"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => {
                        onEdit(goal);
                        setShowDayModal(false);
                      }}
                      className="h-8 w-8 p-0 hover:bg-yellow-100"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center justify-center sm:justify-start space-x-2 sm:space-x-4">
          <Button
            variant="outline"
            iconName="ChevronLeft"
            onClick={() => navigateDate(-1)}
            className="h-8 w-8 sm:h-10 sm:w-10 p-0"
          />
          
          <h3 className="text-base sm:text-lg font-semibold text-foreground min-w-[150px] sm:min-w-[200px] text-center text-sm sm:text-base">
            {viewMode === 'monthly' 
              ? format(selectedDate, 'yyyy', { locale: language === 'pt' ? ptBR : undefined })
              : format(selectedDate, 'MMMM yyyy', { locale: language === 'pt' ? ptBR : undefined })}
          </h3>
          
          <Button
            variant="outline"
            iconName="ChevronRight"
            onClick={() => navigateDate(1)}
            className="h-8 w-8 sm:h-10 sm:w-10 p-0"
          />
        </div>

        <div className="flex items-center justify-center space-x-2">
          <Select
            options={viewModeOptions}
            value={viewMode}
            onChange={setViewMode}
            className="w-32 sm:w-40 text-xs sm:text-sm"
          />
          
          <Button
            variant="outline"
            iconName="Calendar"
            onClick={scrollToToday}
            className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4"
          >
            {labels?.goToToday}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-muted/50 rounded-lg">
        <span className="text-xs sm:text-sm font-medium text-muted-foreground">{labels?.status}:</span>
        {Object.entries(statusColors)?.map(([status, color]) => (
          <div key={status} className="flex items-center space-x-1 sm:space-x-2">
            <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${color}`} />
            <span className="text-xs sm:text-sm text-muted-foreground">{statusLabels?.[status]}</span>
          </div>
        ))}
      </div>

      <div className="relative">
        <div className="absolute top-6 sm:top-8 left-0 right-0 h-0.5 bg-border z-0" />
        
        <div className="flex overflow-x-auto pb-4 sm:pb-6 pt-2 sm:pt-4" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex space-x-2 sm:space-x-4 min-w-full px-2 sm:px-4">
            {timelineDates?.map(date => {
              const dateGoals = getGoalsForDate(date);
              const isCurrentPeriod = viewMode === 'monthly' 
                ? format(date, 'yyyy-MM') === format(new Date(), 'yyyy-MM')
                : isToday(date);
              
              return (
                <div 
                  key={viewMode === 'monthly' ? format(date, 'yyyy-MM') : format(date, 'yyyy-MM-dd')}
                  id={isCurrentPeriod ? 'timeline-today' : undefined}
                  className={`flex flex-col items-center space-y-2 cursor-pointer ${
                    viewMode === 'monthly' ? 'min-w-[80px] sm:min-w-[120px]' : 'min-w-[40px] sm:min-w-[60px]'
                  }`}
                  onClick={() => handleDayClick(date)}
                  onMouseEnter={() => handleMonthHover(date, true)}
                  onMouseLeave={() => handleMonthHover(date, false)}
                >
                  <div className={`relative z-10 w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 transition-all duration-200 hover:scale-110
                                  ${isCurrentPeriod 
                                    ? 'bg-primary border-primary shadow-lg scale-110' 
                                    : dateGoals?.length > 0 
                                      ? 'bg-card border-primary' :'bg-card border-border'
                                  } flex items-center justify-center`}>
                    {isCurrentPeriod && (
                      <div className="w-1 h-1 sm:w-2 sm:h-2 bg-primary-foreground rounded-full" />
                    )}
                  </div>

                  <div className="text-center">
                    <div className={`text-xs sm:text-sm font-medium ${
                      isCurrentPeriod ? 'text-primary' : 'text-foreground'
                    }`}>
                      {viewMode === 'monthly' 
                        ? format(date, 'MMM', { locale: language === 'pt' ? ptBR : undefined })
                        : format(date, 'd')
                      }
                    </div>
                    {viewMode === 'daily' && (
                      <div className="text-xs text-muted-foreground">
                        {format(date, 'EEE', { locale: language === 'pt' ? ptBR : undefined })}
                      </div>
                    )}
                  </div>

                  {dateGoals?.length > 0 && (
                    <div className="flex flex-col items-center space-y-1 sm:space-y-2 mt-2 sm:mt-4">
                      {viewMode === 'monthly' ? (
                        <div className="bg-primary text-primary-foreground px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                          {dateGoals?.length} {dateGoals?.length === 1 ? 
                            (language === 'pt' ? 'meta' : 'goal') : 
                            (language === 'pt' ? 'metas' : 'goals')
                          }
                        </div>
                      ) : (
                        <>
                          {dateGoals?.slice(0, 3)?.map(goal => (
                            <GoalTimelineCard key={goal?.id} goal={goal} date={date} />
                          ))}
                          {dateGoals?.length > 3 && (
                            <button
                              onClick={(e) => {
                                e?.stopPropagation();
                                setSelectedDayGoals(dateGoals);
                                setShowDayModal(true);
                              }}
                              className="text-xs text-muted-foreground bg-muted px-1 sm:px-2 py-0.5 sm:py-1 rounded-full hover:bg-muted-hover transition-colors"
                            >
                              +{dateGoals?.length - 3} {labels?.more}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-border">
        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">{labels?.goalsOfMonth}</h4>
          <p className="text-xl sm:text-2xl font-bold text-primary">
            {viewMode === 'monthly' 
              ? Object.values(groupedGoals)?.flat()?.length
              : Object.entries(groupedGoals)?.filter(([key]) => key?.startsWith(format(selectedDate, 'yyyy-MM')))?.reduce((acc, [, goals]) => acc + goals?.length, 0)
            }
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">{labels?.upcoming}</h4>
          <p className="text-xl sm:text-2xl font-bold text-yellow-600">
            {goals?.filter(goal => {
              if (!goal?.dataAlvo) return false;
              const diff = (parseISO(goal?.dataAlvo) - new Date()) / (1000 * 60 * 60 * 24);
              return diff >= 0 && diff <= 7;
            })?.length || 0}
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
          <h4 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base">{labels?.sectionCompleted}</h4>
          <p className="text-xl sm:text-2xl font-bold text-green-600">
            {goals?.filter(goal => goal?.status === 'concluido')?.length || 0}
          </p>
        </div>
      </div>

      <DayGoalsModal />
    </div>
  );
};

export default TimelineView;